import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import LoadingSpinner from 'components/loading/LoadingSpinner';
import SearchMap from 'components/search/v2/SearchMap/SearchMap';
import { getMaxWidthQuery, getMinQueryWidth, getMinWidthQuery, mediaMMdMin, mediaMSmMax } from 'helpers/breakpoints';
import BoatCard from 'components/search/v2/BoatCard/BoatCard';
import Filter from 'components/search/v2/Filter/Filter';
import BoatsListHeader, { composeHeadline } from 'components/search/v2/BoatsListHeader';
import EmptyResultsMessageWithReset from 'components/search/v2/EmptyResultsMessageWithReset';
import PaginationComponent from 'components/Pagination';
import { FlexCenterBoth } from 'components/primitives/flex';
import {
  useLoadFilterStateFromQuery,
  useSyncPrevBoatsResponse,
  useSyncPushFilterStateToQuery,
} from 'components/search/v2/Filter/hooks';
import {
  areBoatsLoadingAtom,
  currentPageAtom,
  DSUQueryNameAtom,
  filterApiPayloadAtom,
  filteredBoatsAtom,
  filteredBoatsMetaAtom,
  geoTargetsAtom,
  isMapVisibleAtom,
  isRouterReadyAtom,
  nearParamAtom,
  perPageAtom,
  totalBoatsCountAtom,
  zoomLevelAtom,
  selectedExperienceTagAtom,
  ssrDatepickerFlagAtom,
} from 'components/search/v2/Filter/filterStore';
import dynamic from 'next/dynamic';

import {
  bannerHeight,
  breakpoints,
  filterBarHeight,
  filterBarHeightMobile,
  filterBarHeightWithTiles,
  headerHeight,
} from 'components/search/v2/styleConstants';
import ToggleMapSwitch from 'components/search/v2/ToggleMapSwitch';
import { otherFiltersDropdownVisibleAtom } from 'components/search/v2/Filter/contents/OtherFiltersFilterButton';
import useMediaQuerySafe from 'hooks/useMediaQuerySafe';
import { getItunesAppUniversalLinkMetaTag, RENTER_APP_ID } from 'helpers/templateHelpers';
import Head from 'next/head';
import useResetPageOnFilterPayloadChange from 'components/search/v2/hooks/useResetPageOnFilterPayloadChange';
import { RedirectBanner } from 'components/search/v2/RedirectBanner';
import useAbandonEffect from 'utils/tracking/useAbandonEffect';
import DefaultLayout from 'components/layouts/DefaultLayout';
import { getAsString } from 'helpers';
import boatsetterPaths from 'utils/boatsetterPaths';
import FilterPresets from 'components/search/v2/FilterPresets/FilterPresets';
import { useFeatureFlagQueryByNameWithAnonymousID } from 'components/layouts/hooks';
import { trackRudderstackEvent } from 'hooks/useRudderStackTrack';
import {
  constructQueryStringWithoutEncode,
  processQueryOutApi,
  trackSearchBugOccurrence,
} from 'stacks/search/queryHelpers';
import { locationIsDomesticSearch, tokenizeSearchLocationString } from 'stacks/search/helpers';
import { getBoatCardUrl } from 'components/search/v2/utils';
import { useBoatPositionPriceTrack } from 'components/search/v2/hooks/useBoatPositionPriceTrack';
import { __DEV__ } from 'utils';
import ExperienceTiles, { useGetSearchConfig } from 'components/search/v2/Filter/contents/ExperienceTiles';
import axios from 'axios';
import getConfig from 'next/config';
import { API_CLIENT_HEADERS } from 'api/OpenApiConfiguration';
import getDefaultStartDate from 'helpers/dateHelpers';
import { buildFilterPayloadFromQuery, SsrFilterPayload } from 'stacks/search/buildFilterPayloadFromQuery';
import { queryClientAtom } from 'jotai/query';
import { createRudderStackPageEvent } from '../../_app';

const MobileMapToggleButton = dynamic(() => import('components/search/v2/MobileMapToggleButton'), {
  ssr: false,
});

const { publicRuntimeConfig } = getConfig();

const SearchPageV2 = ({
  ssrBoats = [],
  ssrMeta = {} as any,
  ssrPayload = null as SsrFilterPayload | null,
  ssrDatepickerFlag = false,
  ssrSearchConfig = null as any,
  ssrGeoTarget = null as any,
}) => {
  const router = useRouter();

  const totalBoatsCount = useAtomValue(totalBoatsCountAtom);
  const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
  const [isMapVisible, toggleIsMapVisible] = useAtom(isMapVisibleAtom);
  const jotaiBoats = useAtomValue<any[]>(filteredBoatsAtom);
  const jotaiMeta = useAtomValue(filteredBoatsMetaAtom);
  const setSelectedExperienceTag = useSetAtom(selectedExperienceTagAtom);
  const areBoatsLoading = useAtomValue(areBoatsLoadingAtom);
  const near = useAtomValue(nearParamAtom);
  const routerReady = useAtomValue(isRouterReadyAtom);
  const setSsrDatepickerFlag = useSetAtom(ssrDatepickerFlagAtom);
  useEffect(() => {
    if (ssrDatepickerFlag) setSsrDatepickerFlag(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // SSR → Jotai handoff: use SSR data until Jotai produces its first result
  const hasSsrData = ssrBoats.length > 0;
  const [jotaiHasLoaded, setJotaiHasLoaded] = useState(false);
  const jotaiHasLoadedRef = useRef(false);
  useEffect(() => {
    if (routerReady && !areBoatsLoading) {
      // Don't switch to empty Jotai results when we have SSR data.
      // This happens on DSU pages where the search query starts disabled
      // (waiting for geo_target resolution) — disabled queries aren't "loading",
      // so areBoatsLoading is false even though no real fetch has completed.
      if (hasSsrData && jotaiBoats.length === 0) return;

      setJotaiHasLoaded(true);
      jotaiHasLoadedRef.current = true;

      // Dev-mode diagnostic: compare SSR vs client payloads to find mismatches.
      // Only fires once on the initial SSR→Jotai handoff. After that, client-side
      // filter interactions (shallow navs) will naturally diverge from the frozen
      // ssrPayload — that's expected and not worth logging.
      if (__DEV__ && ssrPayload && !jotaiHasLoadedRef.current) {
        const ssrKeys = JSON.stringify(ssrPayload, Object.keys(ssrPayload).sort());
        const clientKeys = JSON.stringify(filterApiPayload, Object.keys(filterApiPayload).sort());
        if (ssrKeys !== clientKeys) {
          console.error('[SSR mismatch] SSR payload:', ssrPayload);
          console.error('[SSR mismatch] Client payload:', filterApiPayload);
          const allKeys = new Set([...Object.keys(ssrPayload), ...Object.keys(filterApiPayload)]);
          allKeys.forEach((k) => {
            const s = JSON.stringify(ssrPayload[k]);
            const c = JSON.stringify(filterApiPayload[k]);
            if (s !== c) console.error(`[SSR mismatch] "${k}": SSR=${s} Client=${c}`);
          });
        }
      }
    }
  }, [routerReady, areBoatsLoading, jotaiBoats.length]);

  const filteredBoats = (() => {
    if (jotaiHasLoaded) return jotaiBoats;
    return hasSsrData ? ssrBoats : jotaiBoats;
  })();
  const filteredBoatsMeta = (() => {
    if (jotaiHasLoaded) return jotaiMeta;
    return hasSsrData ? ssrMeta : jotaiMeta;
  })();
  const geoTargets = useAtomValue(geoTargetsAtom);
  const mapCanShowBreakpoint = useMediaQuerySafe(getMinQueryWidth(breakpoints.MAP_CAN_SHOW));

  const perPage = useAtomValue(perPageAtom);
  const filterApiPayload = useAtomValue(filterApiPayloadAtom);
  const setZoomLevel = useSetAtom(zoomLevelAtom);
  const DSUQueryName = useAtomValue(DSUQueryNameAtom);
  const ssrGeoTargetName = ssrGeoTarget
    ? `${ssrGeoTarget.canonical_name || ssrGeoTarget.display_name}, ${ssrGeoTarget.country_code}`
    : '';
  const headline = composeHeadline(near || DSUQueryName || ssrGeoTargetName);

  const experiencesMetaData = {};
  filteredBoatsMeta?.experience_tags?.forEach((item) => {
    experiencesMetaData[item.key] = item.count;
  });

  const { experienceTagData: clientExperienceTagData } = useGetSearchConfig();
  const experienceTilesData =
    clientExperienceTagData?.length > 0 ? clientExperienceTagData : ssrSearchConfig?.filters?.experience_tags || [];

  // filter out experiences without many options
  const experiencesToShow = experienceTilesData.filter((tile) => experiencesMetaData[tile.key] >= 6);

  const isExperiencesShowing = experiencesToShow.length > 0;

  useEffect(() => {
    if (!isExperiencesShowing) {
      setSelectedExperienceTag(null);
    }
  }, [isExperiencesShowing]);

  // Pre-seed Jotai's internal QueryClient cache with SSR data to prevent double-fetch.
  // Must run before useLoadFilterStateFromQuery (which sets isRouterReadyAtom and triggers atomWithQuery).
  const jotaiQueryClient = useAtomValue(queryClientAtom);
  useEffect(() => {
    if (ssrPayload && ssrBoats.length > 0) {
      jotaiQueryClient.setQueryData(['filteredBoats', ssrPayload], {
        data: { data: ssrBoats, meta: ssrMeta },
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useSyncPrevBoatsResponse();
  useSyncPushFilterStateToQuery(router);
  useLoadFilterStateFromQuery(router);
  useResetPageOnFilterPayloadChange();

  useEffect(() => {
    // Toggle the map when the breakpoint switches
    toggleIsMapVisible(mapCanShowBreakpoint);
  }, [mapCanShowBreakpoint]);

  // url guard
  useEffect(() => {
    if (!router.isReady) return;

    // redirect to HP if either we have 0 params or (near params is missing, and it is not DSU page)
    if (
      !Object.keys(router.query).length ||
      (!router.query.near && !(!!router.query.loc_physical_ms || !!router.query.loc_interest_ms))
    ) {
      router.replace({
        pathname: '/',
        query: router.query,
      });
    }

    // redirect to HP if this condition of DSU passes
    if (!router.query.loc_interest_ms && !!router.query.loc_physical_ms && router.query.ctype === 'om') {
      router.replace({
        pathname: '/',
        query: router.query,
      });
    }
    // checks for global url
    const queryNear = router.query.near as string;
    if (queryNear) {
      const tokens = tokenizeSearchLocationString(queryNear || '');
      const isDomestic = locationIsDomesticSearch(tokens);

      const zoomLevel = router.query.zoom_level as string;
      setZoomLevel(parseInt(zoomLevel, 10));

      if (!isDomestic && router.query.ne_lat && router.query.ne_lng && router.query.sw_lat && router.query.sw_lng) {
        // for some reason global has ne - sw swapped ..... funny
        const globalPayload = {
          near: router.query.near,
          latitudeSw: router.query.sw_lat,
          longitudeSw: router.query.sw_lng,
          latitudeNe: router.query.ne_lat,
          longitudeNe: router.query.ne_lng,
          start_date: router.query.start_date,
          end_date: router.query.end_date,
          availableFrom: undefined,
        };

        if (router.query.trip_date) {
          globalPayload.availableFrom = router.query.trip_date;
        }

        const subdomain = __DEV__ ? 'stg' : 'www';
        window.location.replace(
          `https://${subdomain}.boatsetter.com/global/search?${constructQueryStringWithoutEncode(globalPayload)}`
        );
      }
    }
  }, [router.query]);

  // redirect to HP if we cannot find DSU page
  useEffect(() => {
    if (geoTargets.state === 'hasError') {
      router.replace({
        pathname: '/',
        query: router.query,
      });
    }
  }, [geoTargets]);

  const changePage = useCallback(
    (nextPage) => {
      setCurrentPage(nextPage);
    },
    [setCurrentPage]
  );

  // scroll to top on new search (skip during SSR→Jotai handoff)
  useEffect(() => {
    if (!jotaiHasLoaded) return;
    const root = document.getElementById('__next');
    root.scrollTo(0, 0);
  }, [filteredBoats]);

  // rudder stacks event around scoring (skip during SSR→Jotai handoff)
  useEffect(() => {
    if (!jotaiHasLoaded) return;
    const boats = filteredBoats.map((boat: any, index) => ({
      position: index,
      boatId: boat.boat_public_id,
      score: boat.score,
    }));
    const scorer = filteredBoatsMeta?.scorer;
    const searchProperties = {
      geocoding_result: filteredBoatsMeta?.geocoding_result,
      search_type: filteredBoatsMeta?.search_type,
      radius_search: filteredBoatsMeta?.radius_search,
    };

    if (boats.length > 0)
      createRudderStackPageEvent(router, 1, {
        boats,
        scorer,
        search_properties: JSON.stringify(searchProperties),
      });
  }, [filteredBoats, router]);

  useBoatPositionPriceTrack(filteredBoats, areBoatsLoading, routerReady);

  useAbandonEffect({
    boat_id: null,
    abandon_type: 'plp',
    trip_start: null,
    trip_finish: null,
    conversation_url: null,
    last_searched_plp_url: true,
    last_searched_pdp_url: null,
    abandoned_checkout_url: null,
    last_searched_location: near,
    last_searched_trip_time: router.query.trip_date ? `${router.query.trip_date} 00:00:00` : null,
  });

  useEffect(() => {
    const trackedQueryParams = {
      zoom_level: router.query.zoom_level as string,
      ne_lat: router.query.ne_lat as string,
      ne_lng: router.query.ne_lng as string,
      sw_lat: router.query.sw_lat as string,
      sw_lng: router.query.sw_lng as string,
    };
    trackSearchBugOccurrence(trackedQueryParams, trackRudderstackEvent);
  }, [router.query.zoom_level, router.query.ne_lat, router.query.ne_lng, router.query.sw_lat, router.query.sw_lng]);

  // Show loading overlay when navigating between different search queries on the same route.
  // Without this, old SSR boat cards stay visible while getServerSideProps fetches new data.
  // Uses jotaiHasLoadedRef instead of a timeout — only shows overlay after initial load completes.
  const [isRouteTransitioning, setIsRouteTransitioning] = useState(false);
  useEffect(() => {
    const onStart = (url: string, { shallow }: { shallow: boolean }) => {
      if (!shallow && jotaiHasLoadedRef.current && url.startsWith('/a/boat-rentals/search'))
        setIsRouteTransitioning(true);
    };
    const onDone = () => setIsRouteTransitioning(false);
    router.events.on('routeChangeStart', onStart);
    router.events.on('routeChangeComplete', onDone);
    router.events.on('routeChangeError', onDone);
    return () => {
      router.events.off('routeChangeStart', onStart);
      router.events.off('routeChangeComplete', onDone);
      router.events.off('routeChangeError', onDone);
    };
  }, [router]);

  const showRedirectBanner = getAsString(router.query.redirected_from_pdp) === 'true';

  const { featureFlag: filterPresetsFlag } = useFeatureFlagQueryByNameWithAnonymousID('search_bo_filter_presets');

  return (
    <>
      <Head>{getItunesAppUniversalLinkMetaTag(router.asPath, RENTER_APP_ID)}</Head>
      <Head>
        <meta name="robots" content="noindex, follow" />
      </Head>
      <DefaultLayout noFollow={false} isSearch searchBar hideFooterMobile>
        <ContentLayout
          showTiles={isExperiencesShowing}
          showRedirectBanner={showRedirectBanner}
          mapActive={isMapVisible}
        >
          {showRedirectBanner && <RedirectBanner />}
          <FilterBarWithZIndex showTiles={isExperiencesShowing} showRedirectBanner={showRedirectBanner}>
            {isExperiencesShowing && (
              <ExperienceTiles experiencesToShow={experiencesToShow} location={router.query.near} />
            )}
            <Filter data-testid="search-filter-box" showTiles={isExperiencesShowing} />
            <ToggleSwitchVisibleWrapper className="search-map-toggle" showTiles={isExperiencesShowing}>
              <ToggleMapSwitch isMapVisible={isMapVisible} toggleMap={toggleIsMapVisible} />
            </ToggleSwitchVisibleWrapper>
          </FilterBarWithZIndex>
          <BoatsContainer isMapVisible={isMapVisible}>
            {/* {featureFlags?.top_location_chips && <TopLocationsHeader />} */}

            {filterPresetsFlag && <FilterPresets />}
            <BoatsListHeader
              ssrTotalCount={ssrMeta?.total_count}
              ssrNear={ssrMeta?.total_count ? (router.query.near as string) || ssrGeoTargetName : undefined}
            />

            {filteredBoats.length > 0 ? (
              <BoatsList isMapVisible={isMapVisible}>
                {filteredBoats.map((boat: any, index: number) => (
                  <BoatCard
                    key={boat.boat_public_id}
                    url={getBoatCardUrl({
                      filterApiPayload,
                      boatId: boat.boat_public_id,
                      pos: (currentPage - 1) * perPage + index,
                    })}
                    boat={boat as any}
                    index={index}
                    searchLocation={headline}
                    currentPage={currentPage}
                  />
                ))}
              </BoatsList>
            ) : (
              <EmptyResultsMessageWithReset />
            )}

            {filteredBoats.length > 0 && (
              <StyledPaginationComponent total={totalBoatsCount} handlePageClick={changePage} page={currentPage} />
            )}
          </BoatsContainer>

          <MapWrapper
            className="search-map-wrap"
            isMapVisible={isMapVisible}
            showTiles={isExperiencesShowing}
            data-testid="search-map"
          >
            <SearchMap searchMetaData={filteredBoatsMeta} searchResults={filteredBoats} />
          </MapWrapper>
          {((areBoatsLoading && jotaiHasLoaded) || isRouteTransitioning || (!hasSsrData && !jotaiHasLoaded)) && (
            <Loading />
          )}
        </ContentLayout>
        <MobileMapToggleButton />
      </DefaultLayout>
    </>
  );
};

interface ContentLayoutProps {
  mapActive: boolean;
  showRedirectBanner: boolean;
  showTiles: boolean;
}

const ContentLayout = styled.div<ContentLayoutProps>`
  padding-top: 60px;
  display: grid;
  grid-template-rows: ${({ showTiles }) => (showTiles ? '158px auto' : ' 80px auto')};
  grid-template-areas:
    'filter filter'
    'boats boats';

  ${({ showRedirectBanner, showTiles }) =>
    showRedirectBanner &&
    css`
      grid-template-rows: ${bannerHeight} ${showTiles ? '158px' : '69px'} auto;
      grid-template-areas:
        'redirect-banner redirect-banner'
        'filter filter'
        'boats boats';
      ${mediaMSmMax} {
        grid-template-rows: ${bannerHeight} ${showTiles ? '158px' : '69px'} auto;
      }
    `}

  ${getMinWidthQuery(breakpoints.MAP_CAN_SHOW)} {
    grid-template: ${({ showTiles }) => (showTiles ? '158px auto / auto 32vw' : '80px auto / auto 32vw')};
    grid-template-areas:
      'filter filter'
      '${({ mapActive }) => (mapActive ? 'boats map' : 'boats boats')}';

    ${({ showRedirectBanner, showTiles }) =>
      showRedirectBanner &&
      css<ContentLayoutProps>`
        grid-template: ${bannerHeight} ${showTiles ? '158px' : '69px'} auto / auto 32vw;
        grid-template-areas:
          'redirect-banner redirect-banner'
          'filter filter'
          '${({ mapActive }) => (mapActive ? 'boats map' : 'boats boats')}';

        ${mediaMSmMax} {
          grid-template-rows: ${bannerHeight} ${showTiles ? '158px' : '69px'} auto;
        }
      `}
  }
`;

function FilterBarWithZIndex(props: Omit<React.ComponentProps<typeof FilterBar>, 'otherFiltersVisible'>) {
  const otherFiltersVisible = useAtomValue(otherFiltersDropdownVisibleAtom);
  return <FilterBar {...props} otherFiltersVisible={otherFiltersVisible} />;
}

const FilterBar = styled.div<{ otherFiltersVisible: boolean; showRedirectBanner: boolean; showTiles: boolean }>`
  grid-area: filter;
  background-color: #fff;
  padding: 16px 0;
  position: sticky;
  top: ${({ showRedirectBanner }) => (showRedirectBanner ? `calc(${headerHeight} + ${bannerHeight})` : headerHeight)};
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  z-index: ${({ otherFiltersVisible }) => (otherFiltersVisible ? 2000 : 20)};

  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: ${({ showTiles }) => (showTiles ? '74px auto' : 'auto')};
  row-gap: 16px;

  ${mediaMSmMax} {
    position: fixed;
    width: 100%;
  }

  ${getMinWidthQuery(breakpoints.MAP_CAN_SHOW)} {
    border-bottom: 1px solid #dbdfe5;
    padding-right: 24px;
  }
`;

const BoatsContainer = styled.div<{ isMapVisible: boolean }>`
  grid-area: boats;
  padding: 12px 22px 22px;
  min-height: calc(100vh - 70px - 60px);
  position: relative;

  /* Below MAP_CAN_SHOW the map never shows — always use "no map" padding */
  ${mediaMMdMin} {
    padding: 28px 42px 28px 42px;
  }
  ${getMinWidthQuery(breakpoints.MAP_CAN_SHOW)} {
    padding: 28px ${({ isMapVisible }) => (isMapVisible ? '24px' : '42px')} 28px 42px;
  }
`;

const BoatsList = styled.div<{ isMapVisible: boolean }>`
  display: grid;
  gap: 24px;

  ${getMinWidthQuery(breakpoints.BOATS_TWO_COLS)} {
    grid-template-columns: repeat(2, 1fr);
  }

  /* Below MAP_CAN_SHOW the map never shows — always use the wider "no map" grid.
     This prevents CLS from isMapVisible flipping after hydration. */
  @media (min-width: 960px) and (max-width: ${breakpoints.MAP_CAN_SHOW - 1}px) {
    grid-template-columns: repeat(3, 1fr);
  }

  ${getMinWidthQuery(breakpoints.BOATS_THREE_COLS)} {
    grid-template-columns: repeat(3, 1fr);
  }

  /* Above MAP_CAN_SHOW, column count depends on whether user toggled map off */
  ${({ isMapVisible }) =>
    !isMapVisible &&
    css`
      ${getMinWidthQuery(breakpoints.MAP_CAN_SHOW)} {
        grid-template-columns: repeat(3, 1fr) !important;
      }

      ${getMinWidthQuery(breakpoints.BOATS_FOUR_COLS)} {
        grid-template-columns: repeat(4, 1fr) !important;
      }
    `}
`;

const MapWrapper = styled.div<{ isMapVisible; showTiles: boolean }>`
  grid-area: map;
  max-height: calc(
    100vh - ${headerHeight} - ${({ showTiles }) => (showTiles ? filterBarHeightWithTiles : filterBarHeight)}
  );
  position: sticky;
  top: calc(${headerHeight} + ${({ showTiles }) => (showTiles ? filterBarHeightWithTiles : filterBarHeight)});
  display: ${({ isMapVisible }) => (isMapVisible ? 'block' : 'none')};

  ${getMaxWidthQuery(breakpoints.MAP_CAN_SHOW - 1)} {
    position: fixed;
    inset: 0;
    z-index: 10;
    top: calc(${headerHeight} + ${({ showTiles }) => (showTiles ? filterBarHeightWithTiles : filterBarHeight)});
    max-height: unset;
  }

  ${mediaMSmMax} {
    top: calc(${headerHeight} + ${filterBarHeightMobile});
  }
`;

const Loading = styled((props) => (
  <div {...props}>
    <FlexCenterBoth height="100vh">
      <LoadingSpinner color="#72D4BA" />
    </FlexCenterBoth>
  </div>
))`
  background-color: #0000006c;
  backdrop-filter: blur(2px);
  position: fixed;
  inset: 0;
`;

const ToggleSwitchVisibleWrapper = styled.div<{ showTiles: boolean }>`
  grid-row: ${({ showTiles }) => (showTiles ? 2 : 1)};
  grid-column: 2;
  display: none;

  ${getMinWidthQuery(breakpoints.MAP_CAN_SHOW)} {
    display: block;
  }
`;

const StyledPaginationComponent = styled(PaginationComponent)`
  margin-top: 16px;
`;

export default SearchPageV2;

export const getServerSideProps = async ({ req, query }) => {
  // Redirect DSU ctype=om to homepage (migrated from getInitialProps)
  if (!query.loc_interest_ms && !!query.loc_physical_ms && query.ctype === 'om') {
    return {
      redirect: {
        destination: `/${req.url.replace(boatsetterPaths.search, '')}`,
        statusCode: 302,
      },
    };
  }

  // Kill switch — SSR is on by default; set SSR_SEARCH_DISABLED=true to revert to client-side only
  if (process.env.SSR_SEARCH_DISABLED === 'true') return { props: {} };

  const isDSU = !!(query.loc_physical_ms || query.loc_interest_ms);

  // No search params — will be redirected client-side
  if (!query.near && !isDSU) return { props: {} };

  try {
    // Compute default trip_date when missing (e.g., homepage → search navigation).
    // Prevents SearchBar's non-shallow router.replace from triggering a second getServerSideProps.
    // Uses UTC since we don't have the user's timezone; client corrects via shallow replace if needed.
    const enrichedQuery =
      !query.trip_date && !query.start_date && !query.end_date
        ? { ...query, trip_date: getDefaultStartDate({ timeZone: 'UTC' }).toISOString().split('T')[0] }
        : query;

    let ssrPayload = buildFilterPayloadFromQuery(enrichedQuery);
    const baseUrl = publicRuntimeConfig.LOCAL_CLUSTER_MARKETPLACE;
    // Search endpoint is behind nginx, not marketplace-core directly
    const searchUrl = publicRuntimeConfig.LOCAL_CLUSTER_SEARCH;

    // Forward the anonymous_id cookie so Flipper evaluates group-based flags
    // against the same actor the client would use.
    const anonymousId = req.cookies?.anonymous_id;
    const flagHeaders = {
      ...API_CLIENT_HEADERS,
      ...(anonymousId ? { Cookie: `anonymous_id=${anonymousId}` } : {}),
    };

    // For DSU pages, resolve geo_targets first — the search API needs `near` from the resolved location.
    // Client-side, the search is gated on nearFromGeoTargetsAtom being non-empty.
    let ssrGeoTarget = null;
    if (isDSU) {
      const geoTargetId = query.loc_interest_ms || query.loc_physical_ms;
      const geoTargetParams = query.sem_account_id ? { sem_account_id: query.sem_account_id } : {};
      try {
        const { data } = await axios.get(`${baseUrl}/domestic/v2/geo_targets/${geoTargetId}`, {
          params: geoTargetParams,
          headers: API_CLIENT_HEADERS,
          timeout: 3000,
        });
        ssrGeoTarget = data;
        const resolvedNear = data?.canonical_name || data?.display_name;
        if (resolvedNear) {
          ssrPayload = { ...ssrPayload, near: `${resolvedNear}, ${data.country_code}` };
        }
      } catch {
        // Geo target resolution failed — fall back to client-side
        return { props: {} };
      }
    }

    // Fetch search data, feature flags, and experience config in parallel
    const [searchResult, datepickerFlagResult, searchConfigResult] = await Promise.allSettled([
      axios.get(`${searchUrl}/domestic/v2/search`, {
        params: processQueryOutApi({ ...ssrPayload, include_experience_tags: true }),
        headers: flagHeaders,
        timeout: 3000,
      }),
      axios.get(`${baseUrl}/domestic/v2/flags/test_plp_open_datepicker`, {
        params: { anonymous: true },
        headers: flagHeaders,
        timeout: 3000,
      }),
      axios.get(`${baseUrl}/domestic/v3/configs/search`, {
        headers: API_CLIENT_HEADERS,
        timeout: 3000,
      }),
    ]);

    const searchResponse = searchResult.status === 'fulfilled' ? searchResult.value.data : null;
    const ssrDatepickerFlag =
      datepickerFlagResult.status === 'fulfilled'
        ? datepickerFlagResult.value?.data?.test_plp_open_datepicker ?? false
        : false;

    const ssrSearchConfig = searchConfigResult.status === 'fulfilled' ? searchConfigResult.value?.data ?? null : null;

    // Strip boats to only the fields needed for SSR rendering.
    // The full response is ~128+ kB; this cuts it roughly in half by removing
    // unused fields and redundant photo variants.
    const ssrBoats = (searchResponse?.data ?? []).map((boat) => ({
      boat_public_id: boat.boat_public_id,
      title: boat.title,
      rating: boat.rating,
      completed_booking_count: boat.completed_booking_count,
      instant_bookable: boat.instant_bookable,
      new_boat: boat.new_boat,
      is_top_owner: boat.is_top_owner,
      display_duration: boat.display_duration,
      display_price: boat.display_price,
      display_price_per_hour: boat.display_price_per_hour,
      display_captain_option: boat.display_captain_option,
      price_per_hour_cents: boat.price_per_hour_cents,
      location: boat.location,
      score: boat.score,
      boat_captain: boat.boat_captain ? { status: boat.boat_captain.status } : null,
      photos: (boat.photos ?? []).map((p) => ({
        original: p.original ?? p.standard,
      })),
    }));

    return {
      props: {
        ssrBoats,
        ssrMeta: searchResponse?.meta ?? {},
        ssrPayload: { ...ssrPayload, include_experience_tags: true },
        ssrDatepickerFlag,
        ssrSearchConfig,
        ...(ssrGeoTarget ? { ssrGeoTarget } : {}),
      },
    };
  } catch {
    // Graceful degradation — client-side fetch takes over
    return { props: {} };
  }
};
